import { useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { brand } from "@/data/mockData";

// Client-side SEO helper. Sets <title>, description/keywords, Open Graph +
// Twitter card tags, and a canonical link. For the static pages it also reads
// the admin-editable `seoSettings/{pageKey}` doc from Firestore and lets those
// values override the props (so SEO edits in the admin panel take effect live).
//
// (A future SSR/prerender setup would render these server-side for crawlers
// that don't execute JS — most modern crawlers, incl. Googlebot, do run JS.)

const PATH_TO_KEY = {
  "/": "home",
  "/about": "about",
  "/services": "services",
  "/framework": "framework",
  "/listings": "listings",
  "/results": "results",
  "/testimonials": "testimonials",
  "/awards": "awards",
  "/calculators": "calculators",
  "/blog": "blog",
  "/contact": "contact",
};

const DEFAULT_OG_IMAGE = brand.logoUrl;

function upsertMeta(attr, key, content) {
  if (!content) return;
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function SEO({ title, description, keywords, image }) {
  useEffect(() => {
    let cancelled = false;

    const apply = (t, d, k, img) => {
      if (t) document.title = t;
      const ogImage = img || DEFAULT_OG_IMAGE;
      const url = window.location.href;
      upsertMeta("name", "description", d);
      upsertMeta("name", "keywords", k);
      upsertMeta("property", "og:title", t);
      upsertMeta("property", "og:description", d);
      upsertMeta("property", "og:type", "website");
      upsertMeta("property", "og:url", url);
      upsertMeta("property", "og:image", ogImage);
      upsertMeta("property", "og:site_name", "Johnny Tan · MeProperty");
      upsertMeta("name", "twitter:card", "summary_large_image");
      upsertMeta("name", "twitter:title", t);
      upsertMeta("name", "twitter:description", d);
      upsertMeta("name", "twitter:image", ogImage);
      setCanonical(url.split("?")[0].split("#")[0]);
    };

    // Apply the props synchronously so crawlers/snapshots see something immediately.
    apply(title, description, keywords, image);

    // For the known static pages, let the admin-editable Firestore doc override.
    const key = PATH_TO_KEY[window.location.pathname];
    if (key) {
      getDoc(doc(db, "seoSettings", key))
        .then((snap) => {
          if (cancelled || !snap.exists()) return;
          const s = snap.data();
          apply(
            s.metaTitle || title,
            s.metaDesc || description,
            s.keywords || keywords,
            s.ogImage || image
          );
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [title, description, keywords, image]);

  return null;
}
